import React, { useState, useEffect } from 'react';
import { CheckCircle, Users, Award, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const About = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getText, isEnglish } = useLanguage();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default values
  const defaultAdvantages = [
    { icon: '👥', title: '專業團隊', description: '經驗豐富的專業團隊，為您提供最優質的配對服務' },
    { icon: '🏆', title: '信譽保證', description: '持牌經營，政府認可，信譽良好，服務有保障' },
    { icon: '⏰', title: '24小時支援', description: '全天候客戶支援，隨時解答您的疑問' },
    { icon: '✓', title: '售後保證', description: '兩年合約保證，免費更換服務，讓您放心' }
  ];

  const defaultAdvantagesEn = [
    { icon: '👥', title: 'Professional Team', description: 'Experienced professional team providing you with the best matching services' },
    { icon: '🏆', title: 'Trusted Reputation', description: 'Licensed operation, government recognized, reliable with guaranteed service' },
    { icon: '⏰', title: '24-Hour Support', description: 'Round-the-clock customer support to answer your questions anytime' },
    { icon: '✓', title: 'After-sales Guarantee', description: 'Two-year contract guarantee with free replacement service for your peace of mind' }
  ];

  const defaultServiceFeatures = [
    '親自到海外面試女傭，確保質素',
    '專業配對服務，因應家庭需要',
    '全程跟進簽證申請，省時省力',
    '提供完善培訓，確保工作能力',
    '24小時客戶支援熱線',
    '兩年合約保證及免費更換',
    '協助辦理保險及醫療檢查',
    '定期跟進服務，確保滿意度'
  ];

  const defaultServiceFeaturesEn = [
    'Personal overseas interviews with helpers to ensure quality',
    'Professional matching services tailored to family needs',
    'Full visa application follow-up, saving time and effort',
    'Comprehensive training to ensure work capability',
    '24-hour customer support hotline',
    'Two-year contract guarantee with free replacement',
    'Assistance with insurance and medical examination',
    'Regular service follow-up to ensure satisfaction'
  ];

  const defaultCommitments = [
    { number: '100%', title: '持牌經營', description: '政府認可合法經營' },
    { number: '10+', title: '年經驗', description: '豐富行業經驗' },
    { number: '1000+', title: '成功個案', description: '服務滿意度高' }
  ];

  const defaultCommitmentsEn = [
    { number: '100%', title: 'Licensed', description: 'Government-approved legal operation' },
    { number: '10+', title: 'Years Experience', description: 'Rich industry experience' },
    { number: '1000+', title: 'Success Cases', description: 'High service satisfaction' }
  ];

  // Get advantages with correct language
  const getAdvantages = () => {
    if (!content?.advantages || content.advantages.length === 0) {
      return isEnglish ? defaultAdvantagesEn : defaultAdvantages;
    }
    
    if (isEnglish) {
      // Use titleEn and descriptionEn from each advantage object
      return content.advantages.map((adv, idx) => ({
        icon: adv.icon,
        title: adv.titleEn || defaultAdvantagesEn[idx]?.title || adv.title,
        description: adv.descriptionEn || defaultAdvantagesEn[idx]?.description || adv.description
      }));
    }
    return content.advantages;
  };
  
  // Get service features with correct language
  const getServiceFeatures = () => {
    if (!content?.serviceFeatures || content.serviceFeatures.length === 0) {
      return isEnglish ? defaultServiceFeaturesEn : defaultServiceFeatures;
    }
    
    if (isEnglish) {
      // Use serviceFeaturesEn array if available
      if (content.serviceFeaturesEn && content.serviceFeaturesEn.length > 0) {
        return content.serviceFeaturesEn.map((en, idx) => en || defaultServiceFeaturesEn[idx] || content.serviceFeatures[idx]);
      }
      return defaultServiceFeaturesEn;
    }
    return content.serviceFeatures;
  };
  
  // Get commitments with correct language
  const getCommitments = () => {
    if (!content?.commitments || content.commitments.length === 0) {
      return isEnglish ? defaultCommitmentsEn : defaultCommitments;
    }
    
    if (isEnglish) {
      // Use titleEn and descriptionEn from each commitment object
      return content.commitments.map((com, idx) => ({
        number: com.number,
        title: com.titleEn || defaultCommitmentsEn[idx]?.title || com.title,
        description: com.descriptionEn || defaultCommitmentsEn[idx]?.description || com.description
      }));
    }
    return content.commitments;
  };

  const advantages = getAdvantages();
  const serviceFeatures = getServiceFeatures();
  const commitments = getCommitments();

  // Icon mapping for advantages
  const getIcon = (iconStr) => {
    // If it's an emoji, just display it
    if (iconStr && iconStr.length <= 2) {
      return <span className="text-5xl">{iconStr}</span>;
    }
    // Default icons
    const iconMap = {
      '👥': <Users className="h-12 w-12" style={{ color: '#00b6a5' }} />,
      '🏆': <Award className="h-12 w-12" style={{ color: '#00b6a5' }} />,
      '⏰': <Clock className="h-12 w-12" style={{ color: '#00b6a5' }} />,
      '✓': <CheckCircle className="h-12 w-12" style={{ color: '#00b6a5' }} />,
    };
    return iconMap[iconStr] || <span className="text-5xl">{iconStr}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              <span style={{ color: '#00b6a5' }}>{getText('關於我們', 'About Us')}</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={content?.aboutImage || "https://customer-assets.emergentagent.com/job_homecarer/artifacts/648do6m5_Maid%20photo%20with%20touching%20%283%29.jpg"}
                alt="About Us"
                className="rounded-2xl shadow-xl w-full h-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=700&fit=crop";
                }}
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">
                <span style={{ color: '#00b6a5' }}>
                  {isEnglish 
                    ? (content?.aboutTitleEn || 'Professional Employment Services')
                    : (content?.aboutTitle || '嚴選優質女傭')}
                </span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {isEnglish
                  ? (content?.aboutDescription1En || 'Maidful Employment Agency is a professional employment agency specializing in providing quality foreign domestic helper recruitment services for Hong Kong families. We have rich industry experience and are committed to matching employers with the most suitable helpers.')
                  : (content?.aboutDescription1 || '安芝僱傭中心是一家專業的僱傭中介公司，專門為香港家庭提供優質的外籍家庭傭工聘請服務。我們擁有豐富的行業經驗，致力於為僱主配對最合適的女傭。')}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {isEnglish
                  ? (content?.aboutDescription2En || 'Our services include hiring overseas helpers, contract completion helpers, local helpers, and contract renewal services. Whether you need childcare, elderly care, house cleaning, cooking, or other services, we can find the most suitable candidate for you.')
                  : (content?.aboutDescription2 || '我們的服務包括聘請海外女傭、完約現成女傭、本地現成女傭及續約服務。無論您需要照顧嬰兒、長者，還是需要家居清潔、烹飪等服務，我們都能為您找到最適合的人選。')}
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#00b6a5' }}>
                  {getText('服務宗旨', 'Our Mission')}
                </h3>
                <p className="text-lg text-gray-700 italic">
                  {isEnglish
                    ? (content?.aboutMissionEn || 'Committed to providing the best service experience, comprehensively taking care of our clients\' needs')
                    : (content?.aboutMission || '「致力提供最佳服務體驗，全面照顧客人所需」')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values - 我們的優勢 */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('我們的優勢', 'Our Advantages')}</span>
            </h2>
            <p className="text-xl text-gray-600">
              {isEnglish 
                ? `Why choose ${content?.companyNameEn || 'Maidful'}?`
                : `為什麼選擇${content?.companyName || '安芝'}？`}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-8 pb-6">
                  <div className="flex justify-center mb-4">
                    {getIcon(advantage.icon)}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#00b6a5' }}>
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600">{advantage.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages List - 服務特色 */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>服務特色</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {serviceFeatures.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                <CheckCircle className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#00b6a5' }} />
                <span className="text-lg text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process - 我們的承諾 */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>我們的承諾</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {commitments.map((commitment, index) => (
              <Card key={index} className="text-center p-6">
                <CardContent>
                  <div className="text-6xl font-bold mb-2" style={{ color: '#00b6a5' }}>
                    {commitment.number}
                  </div>
                  <p className="text-xl font-semibold mb-2">{commitment.title}</p>
                  <p className="text-gray-600">{commitment.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #00b6a5 0%, #00d4bd 100%)' }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            讓我們幫您找到最合適的女傭
          </h2>
          <p className="text-xl text-white mb-8">
            24小時客戶支援熱線：{content?.phone || '+852 6187 1388'}
          </p>
          <a
            href={`https://api.whatsapp.com/send?phone=${(content?.whatsapp || '85261871388').replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
              style={{ backgroundColor: '#c8ff00', color: '#000' }}
            >
              立即WhatsApp查詢
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
